// src/components/SelectArticulo.js
import React, { useEffect, useState, useMemo } from 'react';
import { api, endpoints } from '../api/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { t } from '../components/i18n';
import logo from '../assets/LIR-BLANCO.png';
import compassLogo from '../assets/Recurso-8compass-NEGRO.png';
import LangSwitcher from '../components/LangSwitcher';

const TOPBAR_HEIGHT = 120;

const SelectArticulo = ({ sujeto, onAssigned, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Idioma
  const effectiveLang = location.pathname.startsWith('/en') ? 'en' : 'es';
  const dict = (t && t[effectiveLang]) ? t[effectiveLang] : (t?.es || {});
  const isSpanishUI = effectiveLang === 'es';

  // Estados
  const [articulos, setArticulos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [originalSelected, setOriginalSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState('');

  // Saludo con género (consistente con ArticulosList)
  const genero = (sujeto?.genero || '').toLowerCase().trim();
  const saludo = isSpanishUI
    ? (genero.startsWith('fem') ? (dict.greet_f || 'Bienvenida') : (dict.greet_m || 'Bienvenido'))
    : (dict.welcome || 'Welcome');

  // Fetch de artículos disponibles, filtrando vencidos como haces ahora
  useEffect(() => {
    const id = sujeto?.id;
    if (!id) return;

    (async () => {
      try {
         const [disp, asignados] = await Promise.all([
          api.get(endpoints.articulosDisponibles(id), {
            params: { _ts: Date.now() },
          }),
          api.get(endpoints.articulos(id), {
            params: { _ts: Date.now() },
          }),
        ]);

        const arr = Array.isArray(disp.data) ? disp.data : [];
        const now = new Date();
        const futuros = arr.filter((a) => {
          if (!a.fecha_limite) return true;
          const dt = new Date(a.fecha_limite);
          return dt >= now;
        });
        setArticulos(futuros);
        if (futuros.length === 0) {
          setMensaje(dict.no_articles || 'No hay artículos disponibles.');
        }
        
        const asignadosArr = Array.isArray(asignados.data) ? asignados.data : [];
        const asignadosIds = new Set(asignadosArr.map((a) => a.id));
        const pre = futuros.filter((a) => asignadosIds.has(a.id)).map((a) => a.id);
        const inicial = new Set(pre);
        setSelected(inicial);
        setOriginalSelected(new Set(inicial));
      } catch {
        setMensaje(dict.error_fetch_articles || 'Error al obtener artículos.');
        setArticulos([]);
      }
    })();
  }, [sujeto, dict]);

  // Métricas y helpers
  const total = articulos.length;
  const seleccionados = useMemo(() => selected.size, [selected]);
  const hasChanges = useMemo(() => {
    if (originalSelected.size !== selected.size) return true;
    for (const id of selected) {
      if (!originalSelected.has(id)) return true;
    }
    for (const id of originalSelected) {
      if (!selected.has(id)) return true;
    }
    return false;
  }, [selected, originalSelected]);
  const canSave = hasChanges && !loading;

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === articulos.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(articulos.map(a => a.id)));
    }
  };

  const handleSave = async () => {
    if (!sujeto?.id || !hasChanges) return;
    setLoading(true);
    setMensaje('');
    try {
      await api.post(endpoints.asignarArticulos(), {
        id_sujeto: sujeto.id,
        articulos: Array.from(selected),
      });
      const tieneArticulos = selected.size > 0;
      try {
        localStorage.setItem(
          'lir_sujeto',
          JSON.stringify({ ...sujeto, tieneArticulos })
        );
      } catch {}
      setOriginalSelected(new Set(selected));
      onAssigned?.(tieneArticulos);
      navigate(effectiveLang === 'en' ? '/en/articles' : '/es/articulos', { replace: true });
    } catch {
      setMensaje(dict.error_submit || 'Error al guardar la selección.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('lir_sujeto');
      sessionStorage.removeItem('sujeto');
      sessionStorage.removeItem('lir_submitted');
    } catch {}
    if (typeof onLogout === 'function') onLogout();
    navigate(effectiveLang === 'en' ? '/en' : '/es', { replace: true });
  };

  // Cargando / vacío
  const isLoadingEmpty = total === 0 && !mensaje;

  return (
    <div
      style={{
        padding: '0 1.25rem 7.5rem',
        background:
          'radial-gradient(1200px 800px at 15% -10%, rgba(233,63,99,0.12) 0%, rgba(0,0,0,0) 40%), #000',
        color: '#ffffff',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* ===== Topbar consistente ===== */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: TOPBAR_HEIGHT,
          zIndex: 20,
          background:
            'linear-gradient(180deg, rgba(10,10,10,.95) 0%, rgba(10,10,10,.88) 60%, rgba(10,10,10,.80) 100%)',
          borderBottom: '1px solid #2a2a2a',
          backdropFilter: 'blur(6px)',
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: '0 auto',
            height: '100%',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center',
            gap: '1rem',
            padding: '0 1rem',
          }}
        >
          <img src={logo} alt="LIR Logo" style={{ height: 44, objectFit: 'contain', opacity: 0.95 }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ color: '#fff', fontWeight: 800, margin: 0, fontSize: '1.35rem' }}>
                {saludo}, {sujeto?.nombre} {sujeto?.apellido}
              </h2>
            </div>

            <p style={{ color: '#bdbdbd', margin: 0 }}>
              {dict.select_articles_title || 'Selecciona artículos'} · <strong>{seleccionados}</strong> / {total}
            </p>
            <p style={{ color: '#bdbdbd', margin: 0, fontSize: '.9rem' }}>
              {dict.select_articles_hint || '(Selecciona solo los que deseas evaluar)'}
            </p>

            {total > 0 && (
              <div
                aria-hidden
                style={{
                  height: 6,
                  background: '#1a1a1a',
                  borderRadius: 999,
                  overflow: 'hidden',
                  border: '1px solid #2a2a2a',
                  maxWidth: 1000,
                  marginTop: 6,
                }}
              >
                <div
                  style={{
                    width: `${(seleccionados / total) * 100}%`,
                    height: '100%',
                    background: '#E93F63',
                    transition: 'width .25s ease',
                  }}
                />
              </div>
            )}
          </div>

           <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button
              onClick={handleLogout}
              style={{
                background: 'rgba(255,255,255,.08)',
                border: '1px solid #2a2a2a',
                color: '#fff',
                borderRadius: 8,
                padding: '.35rem .6rem',
                cursor: 'pointer',
              }}
            >
              {dict.logout || 'Cerrar sesión'}
            </button>
            <LangSwitcher />
            <img
              src={compassLogo}
              alt="Compass"
              style={{ height: 44, objectFit: 'contain', opacity: 0.95 }}
            />
          </div> 
        </div>
      </div>

      {/* Espaciador para la topbar */}
      <div style={{ height: TOPBAR_HEIGHT + 16 }} />

      {/* ===== Contenido principal ===== */}
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        {/* Card contenedora */}
        <div
          style={{
            border: '1px solid #2a2a2a',
            borderRadius: 12,
            padding: '1.4rem',
            backgroundColor: '#1a1a1a',
            boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
            maxWidth: 980,
            margin: '0 auto',
          }}
        >
          <h3 style={{ color: '#ffffff', fontSize: '1.15rem', margin: 0, fontWeight: 700 }}>
            {dict.select_articles_title || 'Selecciona artículos'}
          </h3>

          {/* Estados: cargando / vacío */}
          {isLoadingEmpty && (
            <p style={{ color: '#bdbdbd', marginTop: '.75rem' }}>
              {dict.loading_articles || 'Cargando artículos…'}
            </p>
          )}
          {mensaje && (
            <p style={{ color: '#ff6b6b', marginTop: '.75rem' }}>
              {mensaje}
            </p>
          )}

          {/* Acciones en bloque */}
          {total > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={toggleAll}
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid #2a2a2a',
                  color: '#fff',
                  fontSize: '.9rem',
                  padding: '.45rem .7rem',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                {selected.size === articulos.length
                  ? (dict.unselect_all || 'Deseleccionar todo')
                  : (dict.select_all || 'Seleccionar todo')}
              </button>

              <span style={{ color: '#bdbdbd', fontSize: '.95rem' }}>
                {(dict.selected || 'Seleccionados')}: <strong>{seleccionados}</strong> / {total}
              </span>
            </div>
          )}

          {/* Lista de artículos */}
          <div style={{ display: 'grid', gap: 10, marginTop: '1rem' }}>
            {articulos.map((a, i) => {
              const checked = selected.has(a.id);
              return (
                <label
                  key={a.id ?? i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    gap: 10,
                    alignItems: 'center',
                    padding: '.6rem .75rem',
                    border: '1px solid #2a2a2a',
                    borderRadius: 10,
                    background: checked ? '#181317' : '#161616',
                    transition: 'background-color .2s ease, border-color .2s ease',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(a.id)}
                    style={{
                      width: 18,
                      height: 18,
                      accentColor: '#E93F63',
                      cursor: 'pointer',
                    }}
                  />
                  <div style={{ color: '#e5e5e5', lineHeight: 1.35 }}>
                    <div style={{ fontWeight: 700 }}>
                      {a.area}
                    </div>
                    {/* {a.fecha_limite && (
                      <div style={{ color: '#bdbdbd', fontSize: '.9rem', marginTop: 2 }}>
                        {(dict.deadline || 'Fecha límite')}: {new Date(a.fecha_limite).toLocaleDateString()}
                      </div>
                    )} */}
                  </div>

                  {/* Badge simple de estado */}
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: '.75rem',
                      fontWeight: 700,
                      border: '1px solid rgba(189,189,189,.35)',
                      color: checked ? '#E93F63' : '#bdbdbd',
                      background: checked ? 'rgba(233,63,99,.12)' : 'rgba(189,189,189,.08)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {checked ? (dict.selected || 'Seleccionado') : (dict.available || 'Disponible')}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CTA inferior fijo (coincide con ArticulosList) ===== */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(10,10,10,.85) 20%, rgba(10,10,10,.95) 100%)',
          borderTop: '1px solid #2a2a2a',
          zIndex: 10,
          backdropFilter: 'blur(6px)',
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: '0 auto',
            padding: '0.75rem 1rem',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center',
            columnGap: '0.75rem',
          }}
        >
          <div style={{ justifySelf: 'start' }} />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ color: '#bdbdbd', fontSize: '.95rem' }}>
              {(dict.selected || 'Seleccionados')}: <strong>{seleccionados}</strong> / {total}
            </span>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                backgroundColor: canSave ? '#E93F63' : '#bdbdbd',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.2rem',
                borderRadius: 10,
                fontWeight: 800,
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontSize: '0.95rem',
                transition: 'background-color .25s ease, transform .1s ease',
                boxShadow: canSave ? '0 6px 16px rgba(233,63,99,.35)' : 'none',
              }}
              aria-label={dict.save || 'Guardar'}
            >
              {loading ? (dict.saving || 'Guardando…') : (dict.save || 'Guardar')}
            </button>
          </div>

          <div style={{ justifySelf: 'end', width: 0, height: 0 }} />
        </div>
      </div>

      {/* Toast de error (solo si aplica) */}
      {mensaje && (
        <div
          role="status"
          style={{
            position: 'fixed',
            right: 16,
            bottom: 86,
            background: 'rgba(255,68,68,.15)',
            border: '1px solid rgba(255,68,68,.35)',
            color: '#ff4444',
            padding: '.75rem 1rem',
            borderRadius: 12,
            backdropFilter: 'blur(6px)',
            animation: 'fadeIn .25s ease',
            zIndex: 15,
          }}
        >
          {mensaje}
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn{from{opacity:0; transform:translateY(4px)}to{opacity:1; transform:translateY(0)}}
          /* Acentos de inputs en dark */
          input[type="checkbox"]:focus { outline: none; }
        `}
      </style>
    </div>
  );
};

export default SelectArticulo;
